import axios from "./axios";

/**
 * Axios interceptor (sessionExpiredDialog) resolves failed HTTP responses as fulfillment
 * by passing `error.response`, so 4xx/5xx often do not hit `.catch()`.
 */
export function isPromangApiErrorResponse(res) {
  if (!res || typeof res.status !== "number") return false;
  if (res.status >= 400) return true;
  const errs = res?.data?.errors;
  return Array.isArray(errs) && errs.length > 0;
}

export function promangApiErrorsMessage(res, fallback) {
  const raw = res?.data?.errors;
  if (Array.isArray(raw) && raw.length) return raw.join(", ");
  if (typeof raw === "string" && raw) return raw;
  return fallback;
}

export function promangApiErrorsMessageFromCaught(err, fallback) {
  const res = err?.response ?? err;
  return promangApiErrorsMessage(res, fallback);
}

export const fetchWorkOrders = async (controller) => {
  return axios({ method: "get", url: "/work_orders/", params: controller })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      throw error;
    });
};

export async function createWorkOrder(payload, params = {}) {
  return axios
    .post(`/work_orders`, { work_order: payload }, { params })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      throw error;
    });
}

export async function updateWorkOrder(payload, options = {}) {
  const { params, ...axiosConfig } = options;
  return axios
    .put(
      `/work_orders/${payload.id}`,
      { work_order: payload },
      { params, ...axiosConfig }
    )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      throw error;
    });
}

export async function fetchWorkOrder(payload, params = {}) {
  return axios.get(`/work_orders/${payload}`, { params });
}

export async function markWorkOrderReadyForInvoicing(workOrderId, billingPreparation) {
  return axios.post(
    `/work_orders/${workOrderId}/mark_ready_for_invoicing`,
    { billing_preparation: billingPreparation }
  );
}

export async function createWorkOrderSalesOrder(workOrderId, billingPreparation) {
  return axios.post(`/work_orders/${workOrderId}/create_sales_order`, {
    billing_preparation: billingPreparation,
  });
}

/** Company admin only — correct status without creating a sales order (legacy invoiced, etc.). */
export async function workOrderAdminSetStatus(workOrderId, { status, is_completed } = {}) {
  const body = { status };
  if (is_completed !== undefined && is_completed !== null) {
    body.is_completed = is_completed;
  }
  return axios.post(`/work_orders/${workOrderId}/admin_set_status`, body);
}

export async function bulkCreateWorkOrderSalesOrders({
  workOrderIds,
  strategy,
  billingPreparation = {},
}) {
  return axios.post(`/work_orders/bulk_create_sales_orders`, {
    work_order_ids: workOrderIds,
    strategy,
    billing_preparation: billingPreparation,
  });
}

export async function deleteWorkOrder(payload) {
  return axios
    .delete(`/work_orders/${payload}`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      throw error;
    });
}

export async function printWorkOrder(payload) {
  return axios.get(`/work_orders/${payload.id}/pdf`, {
    params: {
      printWorkOrderType: payload.printWorkOrderType,
      include_images: payload.include_images,
    },
  });
}

export async function getPdfWorkOrder(payload) {
  return axios.get(`/work_orders/${payload.id}/pdf`, {
    params: payload.params,
    type: "application/pdf",
  });
}

// Work Order Items API functions (similar to report items)
export async function fetchWorkOrderItems(workOrderId, params = {}) {
  return axios.get(`/work_orders/${workOrderId}/items`, { params: params });
}

export async function fetchWorkOrderItem(workOrderId, itemId, params = {}) {
  return axios.get(`/work_orders/${workOrderId}/items/${itemId}`, { params: params });
}

export async function updateWorkOrderItem(workOrderId, item) {
  if (!item || !item.id) {
    throw new Error("Item ID is required");
  }
  
  return axios
    .put(`/work_orders/${workOrderId}/items/${item.id}`, { 
      item: {
        quantity: item.quantity,
        description: item.description,
        unit_id: item.unit_id,
        sales_price: item.sales_price,
        purchase_price: item.purchase_price,
        discount: item.discount,
        surcharge: item.surcharge,
        product_id: item.product_id,
        taken_in_vehicle: item.taken_in_vehicle || false,
        not_used: item.not_used || false,
        quantity_used: item.quantity_used || null,
        quantity_not_used: item.quantity_not_used || null,
        returned_to_storage: item.returned_to_storage || false,
        left_in_vehicle: item.left_in_vehicle || false,
        storage_id: item.storage_id || null,
        picked: item.picked || false,
        picked_at: item.picked_at || null,
      }
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error in updateWorkOrderItem:", error);
      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors.join(", "));
      }
      throw error;
    });
}

export async function addWorkOrderItems(workOrderId, items) {
  // For work orders, we use the update endpoint with order_items
  // Format items for the backend (order_items expects specific format)
  const formattedItems = items.map(item => ({
    id: item.id || null,
    product_id: item.product_id || null,
    title: item.title || null,
    description: item.description || null,
    quantity: item.quantity || 0,
    sales_price: item.sales_price || null,
    purchase_price: item.purchase_price || null,
    discount: item.discount || null,
    surcharge: item.surcharge || null,
    unit_id: item.unit_id || null,
    group_row: item.group_row || null,
    is_quote_line: item.is_quote_line || false,
    // Include consumption-related fields with defaults for new items
    picked: item.picked !== undefined ? item.picked : false,
    picked_at: item.picked_at || null,
    taken_in_vehicle: item.taken_in_vehicle !== undefined ? item.taken_in_vehicle : false,
    not_used: item.not_used !== undefined ? item.not_used : false,
    quantity_used: item.quantity_used || null,
    quantity_not_used: item.quantity_not_used || null,
    returned_to_storage: item.returned_to_storage !== undefined ? item.returned_to_storage : false,
    left_in_vehicle: item.left_in_vehicle !== undefined ? item.left_in_vehicle : false,
    storage_id: item.storage_id || null,
    invoiced: item.invoiced !== undefined ? item.invoiced : false,
  }));
  
  return axios
    .put(`/work_orders/${workOrderId}`, { 
      work_order: {
        order_items: formattedItems
      }
    })
    .then((response) => {
      // Return the items from the response
      const workOrder = response.data?.data || response.data;
      return {
        data: {
          items: workOrder?.items || items
        }
      };
    })
    .catch((error) => {
      console.error("Error in addWorkOrderItems:", error);
      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors.join(", "));
      }
      throw error;
    });
}

export async function deleteWorkOrderItem(workOrderId, itemId) {
  return axios
    .delete(`/work_orders/${workOrderId}/items/${itemId}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error in deleteWorkOrderItem:", error);
      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors.join(", "));
      }
      throw error;
    });
}

export async function createWorkOrderChecklist(payload) {
  const id = payload?.work_order?.id;
  return axios.post(`/work_orders/${id}/createChecklist`, payload);
}

export async function deleteWorkOrderChecklist(workOrderId) {
  return axios.delete(`/work_orders/${workOrderId}/deleteChecklist`);
}

export async function fetchWorkOrderChecklist(workOrderId, params = {}) {
  return axios.get(`/work_orders/${workOrderId}/checklist`, { params });
}

export async function updateWorkOrderChecklist(workOrderId, payload) {
  return axios.put(`/work_orders/${workOrderId}/update_checklist`, {
    work_order: payload,
  });
}
